import { Button } from "@capacit-com/lara-law-shared-ui";
import { FC, useState } from "react";
import { Plus } from "lucide-react";
import ToneOfVoice from "@/models/tone-of-voice/toneOfVoice";
import { useToneOfVoiceCrud } from "./hooks/useToneOfVoiceCrud";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import { ToneOfVoiceRowSkeleton } from "./components/ToneOfVoiceSkeleton";
import { DataTable } from "@/components/data-table/data-table";
import useToneOfVoiceTableData from "./hooks/useToneOfVoiceTableData";
import { ToneOfVoiceColumns } from "./components/ToneOfVoiceColumns";
import CreateAndEditToneOfVoiceDialog from "./components/CreateAndEditToneOfVoiceDialog";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import PageContent from "@/pages/PageContent";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";

const ToneOfVoiceView: FC = () => {
  // Hooks
  const { handleDelete, isDeleting } = useToneOfVoiceCrud();
  const {
    data,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    search,
    handleSearchChange,
    setPageIndex,
    setPageSize,
    setSorting,
    isLoading,
  } = useToneOfVoiceTableData();
  const hasPermission = useHasPermission();

  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [toneOfVoice, setToneOfVoice] = useState<ToneOfVoice | null>(null);
  const [isDeletingDialogOpen, setIsDeletingDialogOpen] = useState(false);

  const handleOpenCreationDialog = () => {
    setToneOfVoice(null);
    setIsCreationOpen(true);
  };

  const handleRowClick = (rowIndex: number) => {
    setToneOfVoice(data[rowIndex]);
    setIsCreationOpen(true);
  };

  const handleDeleteDialogOpen = (toneOfVoiceToDelete: ToneOfVoice) => {
    setToneOfVoice(toneOfVoiceToDelete);
    setIsDeletingDialogOpen(true);
  };

  return (
    <PageContent title="Tone of voice">
      <div className="flex flex-col h-full space-y-2 p-4 xl:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-foreground">
            Tone of voice
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Definér den sproglige stil, chatbotten svarer i. En tone of voice
            ændrer sprogets stil og tone – ikke strukturen eller indholdet i
            svarene.
          </p>
        </div>
        <DataTable
          columns={ToneOfVoiceColumns(
            handleDeleteDialogOpen,
            hasPermission({ allOf: [Permission.DeleteToneOfVoice] })
          )}
          data={data}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          sorting={sorting}
          search={search}
          handleSearchChange={handleSearchChange}
          setSorting={setSorting}
          handleRowClick={
            hasPermission({ allOf: [Permission.UpdateToneOfVoice] })
              ? handleRowClick
              : undefined
          }
          isLoading={isLoading}
          skeletonComponent={ToneOfVoiceRowSkeleton}
          stickyHeader={true}
          createNode={
            <PermissionGate allOf={[Permission.CreateToneOfVoice]}>
              <Button
                size="sm"
                className="ml-2"
                onClick={handleOpenCreationDialog}
              >
                <Plus className="h-4 w-4" />
                Opret tone of voice
              </Button>
            </PermissionGate>
          }
        />
        <PermissionGate
          oneOf={[Permission.CreateToneOfVoice, Permission.UpdateToneOfVoice]}
        >
          <CreateAndEditToneOfVoiceDialog
            isOpen={isCreationOpen}
            onOpenChange={setIsCreationOpen}
            toneOfVoice={toneOfVoice}
            setToneOfVoice={setToneOfVoice}
          />
        </PermissionGate>
        <PermissionGate allOf={[Permission.DeleteToneOfVoice]}>
          <ConfirmationDialog
            title="Slet tone of voice"
            description={`Er du sikker på, at du vil slette ${
              toneOfVoice?.title ? toneOfVoice.title : "denne tone of voice"
            }?`}
            isOpen={isDeletingDialogOpen}
            onClose={() => setIsDeletingDialogOpen(false)}
            confirmText="Slet"
            onConfirm={async () => {
              if (toneOfVoice) {
                await handleDelete(toneOfVoice.id);
                setIsDeletingDialogOpen(false);
              }
            }}
            confirmPending={isDeleting}
            variant="destructive"
          />
        </PermissionGate>
      </div>
    </PageContent>
  );
};

export default ToneOfVoiceView;
